import { useQuery } from "@tanstack/react-query";
import { USER_API } from "config";
import { useAxios } from "hooks/useAxios";
import { useProjectVersions, type ProjectVersionSummary } from "hooks/useProjects/useVersions";
import { scriptKeys } from "queries";

export type DocumentCommentCount = { thread_count: number; open_thread_count: number };

/** The version uploaded just before `documentId`, or null when it is the first one. */
export function previousVersion(
  versions: ProjectVersionSummary[],
  documentId: string
): ProjectVersionSummary | null {
  const newestFirst = [...versions].sort(
    (a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
  );
  const index = newestFirst.findIndex((version) => version.document_id === documentId);
  if (index < 0) return null;
  return newestFirst[index + 1] ?? null;
}

/** "3 comment threads on the previous version", or null when there is nothing to say. */
export function previousVersionCommentsLabel(
  count: DocumentCommentCount | undefined,
  version: ProjectVersionSummary | null
): string | null {
  if (!count || !version || count.thread_count <= 0) return null;
  const threads = count.thread_count === 1 ? "comment thread" : "comment threads";
  return `${count.thread_count} ${threads} on the previous version`;
}

/**
 * Comments stay with the version they were written on. When this version is new, tell the user
 * the previous one has threads so they know where to look.
 */
export function usePreviousVersionCommentsLabel(projectId: string, documentId: string) {
  const axios = useAxios();
  const versionsQuery = useProjectVersions(projectId || undefined);
  const prev = documentId ? previousVersion(versionsQuery.data ?? [], documentId) : null;
  const prevDocumentId = prev?.document_id ?? "";

  const countQuery = useQuery<DocumentCommentCount>({
    enabled: Boolean(projectId && prevDocumentId),
    queryKey: scriptKeys.commentCount(projectId, prevDocumentId),
    queryFn: async () => {
      const { data } = await axios.get(USER_API.getDocumentCommentCount(projectId, prevDocumentId));
      return data as DocumentCommentCount;
    },
    staleTime: 5 * 60 * 1000,
  });

  return previousVersionCommentsLabel(countQuery.data, prev);
}
